<template>
  <div class="w-full flex flex-col gap-2">
    <template v-if="!isFastMode">
      <div v-if="!tab.open" class="flex gap-2">
        <l-button class="w-full" @click="reopen">
          {{ t('tabs.reopen') }}
        </l-button>
        <l-button color="yellow" class="w-full" @click="bill">
          {{ t('tabs.bill') }}
        </l-button>
      </div>
      <l-button v-else-if="!tab.activationTime && !editing" class="w-full" @click="activate">
        {{ t('tabs.activate') }}
      </l-button>
      <template v-else>
        <div v-if="canClose && !editing" class="flex flex-col gap-2">
          <l-button class="w-full" @click="closeTab">
            {{ closeText }}
          </l-button>
          <div class="flex gap-2">
            <l-button v-if="config.enableKitchenOrders" color="red" class="w-full" @click="order">
              {{ t('tabs.order') }}
              <div
                v-if="pendingOrders || ordersNotPrinted"
                class="absolute top-0 right-0 border-2 border-n-0 rounded-full notification-circle bg-y-500 w-3 h-3 m-2"
              />
            </l-button>
            <l-button color="yellow" class="w-full" @click="bill">
              {{ t('tabs.bill') }}
            </l-button>
          </div>
        </div>
        <div
          v-else
          class="flex gap-2"
          :class="[config.enableKitchenOrders ? 'flex-col' : 'flex-row']"
        >
          <l-button
            class="w-full"
            :class="{
              hidden: isDeliveryOrOwnDelivery && isMobile
            }"
            @click="goToCheckout"
          >
            {{ checkoutText }}
          </l-button>
          <div v-if="showDefaultActions" class="flex gap-2 w-full">
            <l-button v-if="config.enableKitchenOrders" color="red" class="w-full" @click="order">
              {{ t('tabs.order') }}
              <div
                v-if="pendingOrders || ordersNotPrinted"
                class="absolute top-0 right-0 border-2 border-n-0 rounded-full notification-circle bg-y-500 w-3 h-3 m-2"
              />
            </l-button>
            <l-button color="yellow" class="w-full" @click="bill">
              {{ t('tabs.bill') }}
            </l-button>
          </div>
        </div>
      </template>
    </template>
    <template v-else>
      <div class="flex flex-col w-full gap-2">
        <div class="flex gap-2">
          <l-button
            v-for="paymentMethod in fastModePaymentMethod"
            :key="paymentMethod.type"
            class="w-full"
            color="yellow"
            @click="useFastMode().doFastPayment(paymentMethod.type)"
          >
            {{ paymentMethod.name }}
          </l-button>
        </div>
        <l-button class="w-full" @click="useFastMode().doCheckout()">
          {{ t('tabs.checkout') }}
        </l-button>
      </div>
    </template>

    <resend-kitchen-orders
      v-if="showResendKitchenOrders"
      :tab-id="tabId"
      @close="showResendKitchenOrders = false"
    />
    <print-bills v-if="showPrintBills" :tab-id="tabId" @close="showPrintBills = false" />
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, ref, toRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { LButton, useTracker } from '@last/core-ui/paprika'

import { useCheckout } from '@/composables/useCheckout'
import { useKitchenOrders } from '@/composables/useKitchenOrders'
import { useTabs } from '@/composables/useTabs'
import PrintBills from '@/modules/ordering/PrintBills.vue'
import ResendKitchenOrders from '@/modules/ordering/ResendKitchenOrders.vue'
import { useConfigStore } from '@/store/config'
import { useFastMode } from '@/store/fastMode'
import { useTabsStore } from '@/store/tabs'
import { isMobile } from '@/utils/device-type'

interface Props {
  tabId: string
  editing?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  editing: false
})

const tabsStore = useTabsStore()
const configStore = useConfigStore()
const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const { config } = storeToRefs(configStore)
const tracker = useTracker()

const isFastMode = computed(() => {
  return route.name === 'fastMode'
})

const tabId = toRef(props, 'tabId')

const { activateTab, reopenTab } = tabsStore
const {
  tab,
  allBills,
  isFullyPaid,
  hasNonBilledProducts,
  fastCheckoutAllowed,
  printBill,
  closeTab
} = useTabs(tabId)
const { pendingOrders, ordersNotPrinted, sendKitchenOrder } = useKitchenOrders(tabId)
const { paymentMethods } = useCheckout(tabId.value)

const showResendKitchenOrders = ref(false)
const showPrintBills = ref(false)

const isDeliveryOrOwnDelivery = computed(() => {
  return tab.value.pickupType && ['delivery', 'ownDelivery'].includes(tab.value.pickupType)
})

const fastModePaymentMethod = computed(() => {
  return paymentMethods.value.slice(0, 2)
})

async function goToCheckout() {
  const hasNewCheckout = await tracker.hasFeatureFlag('new-checkout')
  if (isDeliveryOrOwnDelivery.value) {
    router.push({
      name: hasNewCheckout ? 'deliveryCheckout2' : 'deliveryCheckout',
      params: { tabId: props.tabId }
    })
  } else {
    if (fastCheckoutAllowed.value) {
      router.push({
        name: hasNewCheckout ? 'checkout2' : 'fastCheckout',
        params: { tabId: props.tabId }
      })
    } else {
      router.push({
        name: hasNewCheckout ? 'checkout2' : 'checkout',
        params: { tabId: props.tabId }
      })
    }
  }
}

function printKitchenOrder() {
  sendKitchenOrder('all')
}

function order() {
  if (pendingOrders.value) {
    printKitchenOrder()
    if (route.name !== 'pos') {
      router.push({
        name: 'pos'
      })
    }
  } else {
    showResendKitchenOrders.value = true
  }
}

function activate() {
  activateTab({ tab: tab.value, sendEvent: true })
}

function reopen() {
  reopenTab({ tabId: props.tabId })
  router.push({
    name: 'orderManagement',
    params: { tabId: props.tabId }
  })
}

function bill() {
  if (allBills.value.length === 0) {
    printBill()
  } else {
    showPrintBills.value = true
  }
}

const closeText = computed(() => {
  let text = t('tabs.close')
  if (tab.value.deliveryOrder?.preferredPaymentMethod) {
    text = `${t('tabs.close-with')} ${t(`bill.${tab.value.deliveryOrder.preferredPaymentMethod}`, tab.value.deliveryOrder.preferredPaymentMethod)}`
  }
  return text
})

const checkoutText = computed(() => {
  return isDeliveryOrOwnDelivery.value ? t('tabs.delivery-checkout') : t('tabs.checkout')
})

const canClose = computed(() => {
  return (
    isFullyPaid.value ||
    (!hasNonBilledProducts.value && tab.value.deliveryOrder?.preferredPaymentMethod)
  )
})

const showDefaultActions = computed(() => {
  // We only show default actions for delivery on editing mode or mobile
  return (
    tab.value.activationTime && (!isDeliveryOrOwnDelivery.value || !props.editing || isMobile.value)
  )
})
</script>
